import { balance } from './balance';
import type { PlayOutcome } from './play';
import type {
  AttackUse,
  Combatant,
  DefenseSlot,
  GameEvent,
  GameState,
  MatchupResult,
  OffenseSlot,
  PlayEvent,
  PossessionReason,
  ScoreKind,
  Team,
  TeamId,
  UnitId,
} from './types';

/**
 * The battle log, Showdown style: one line per thing that happened, in time order, with both
 * sides' numbers for every matchup. The client shows these lines verbatim (PRD S5), so the
 * wording lives here, next to the events it describes.
 */
export function formatPlayLog(outcome: PlayOutcome, before: GameState): string[] {
  return playLogEntries(outcome, before).map((e) => e.line);
}

/**
 * A log line and the index of the event it describes, so a client can show it as that event plays.
 * `line` is the whole Showdown-style line; `text` says the same in plain English and `numbers`
 * holds the arithmetic behind it (battles and throws only), for a client that shows them apart.
 */
export interface LogEntry {
  event: number;
  line: string;
  text: string;
  numbers?: string;
}

/** formatPlayLog with each line tied to its event; the result line belongs to the whistle. */
export function playLogEntries(outcome: PlayOutcome, before: GameState): LogEntry[] {
  const who = (u: UnitId): Combatant => {
    const [side, slot] = u.split(':') as ['off' | 'def', string];
    return side === 'off'
      ? before.offense.lineup[slot as OffenseSlot]
      : before.defense.lineup[slot as DefenseSlot];
  };
  const name = (u: UnitId) => who(u).name;
  const entries: LogEntry[] = [];
  outcome.events.forEach((e, event) => {
    const line = e.type === 'whistle' ? summary(outcome) : describe(e, name, who, before.ballOn);
    if (line) entries.push({ event, line, ...plain(e, line, name) });
  });
  return entries;
}

/** A line's plain English and, for battles and throws, its numbers apart. */
function plain(
  e: PlayEvent,
  line: string,
  name: (u: UnitId) => string,
): { text: string; numbers?: string } {
  if (e.type === 'battle') {
    const m = e.matchup;
    let text = `${name(e.a)} ${VERB[m.kind]} ${name(e.b)}: ${name(m.winner === 'a' ? e.a : e.b)} wins.`;
    if (m.crit) text += ' A critical hit!';
    if (m.superEffective) text += " It's super effective!";
    return { text, numbers: sides(m, gangNote(e, name)) };
  }
  if (e.type === 'throw') {
    const text =
      `${name(e.from)} throws ${e.hot ? 'hot ' : ''}to ${name(e.to)} at ${secs(e.t)}` +
      `${e.pressured ? ' under pressure' : ''}. ${e.onTarget ? 'On target.' : 'Off target.'}`;
    return { text, numbers: throwOdds(e) };
  }
  return { text: line };
}

/** A throw's numbers: the accuracy and its roll, or the throw battle's crit that decided it. */
const throwOdds = (e: Extract<PlayEvent, { type: 'throw' }>) =>
  e.crit === 'a'
    ? 'a critical throw, no roll'
    : e.crit === 'b'
      ? 'a critical read, no roll'
      : `${Math.round(e.accuracy * 100)}% needed, roll ${e.roll.toFixed(2)}`;

/** Steps of separation: whole in steps mode, one place in continuous (slice 10g). */
const stepsText = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(1));

const n = (x: number) => String(Math.round(x));
const secs = (t: number) => `${t.toFixed(1)} s`;
/** The multiplier as the battle used it: "2.0", "0.5", "0.25", "1.23" (slice 10a: two places). */
const mult = (m: number) =>
  Number.isInteger(m * 10) ? m.toFixed(1) : String(Number(m.toFixed(2)));

const STAT_LABEL = { atk: 'Atk', def: 'Def', spa: 'Sp. Atk', spd: 'Sp. Def', spe: 'Spe' } as const;

/**
 * One side's numbers. Position battles: "base x type x roll = total". When an attack decided the
 * numbers, the base is the stat, labelled, with the attack's power multiplier and STAB pulled out:
 * "140 (Atk) x 1.4 x 1.5 STAB x 2.0 x 0.93 = 547".
 */
function side(
  base: number,
  typeMult: number,
  roll: number,
  total: number,
  stat: MatchupResult['aStat'],
  attack: AttackUse | undefined,
  note = '',
  withBase?: number,
): string {
  const tail = `${mult(typeMult)}${note} x ${roll.toFixed(2)} = ${n(total)}`;
  // The averaged gang form (slice 10q): the base is two reads averaged, shown as both.
  if (withBase !== undefined) {
    return `(${n(2 * base - withBase)} + ${n(withBase)}) / 2 x ${tail}`;
  }
  if (stat === undefined) return `${n(base)} x ${tail}`;
  if (!attack?.hit) return `${n(base)} (${STAT_LABEL[stat]}) x ${tail}`;
  const stab = attack.stab === 1 ? '' : ` x ${mult(attack.stab)} STAB`;
  const raw = base / attack.powerMult / attack.stab;
  return `${n(raw)} (${STAT_LABEL[stat]}) x ${mult(attack.powerMult)}${stab} x ${tail}`;
}

/**
 * Both sides' numbers: "120 x 1.0 x 0.93 = 112 vs 98 x 2.0 x 0.97 = 190". `bNote` goes after the
 * b side's type multiplier (a gang tackle's mod).
 */
function sides(m: MatchupResult, bNote = ''): string {
  const a = side(m.aBase, m.aTypeMult, m.aRoll, m.aStrength, m.aStat, m.aAttack);
  const b = side(
    m.bBase,
    m.bTypeMult,
    m.bRoll,
    m.bStrength,
    m.bStat,
    m.bAttack,
    bNote,
    m.bWithBase,
  );
  return `${a} vs ${b}`;
}

/**
 * A gang tackle's mod on the tackler's side, with the helper's name: " x 1.25 (Gengar)". Read back
 * from the numbers, and left out when it is 1 (a gang tackle with no bonus). The averaged form
 * (slice 10q) says whose strength went in: " x 1.25 (with Gengar)"; the per-man form says how many
 * were counted: " x 1.50 (2 helpers: Gengar, Starmie)".
 */
function gangNote(e: Extract<PlayEvent, { type: 'battle' }>, name: (u: UnitId) => string): string {
  if (e.gang === undefined) return '';
  const m = e.matchup;
  const mod = Math.round((m.bStrength / (m.bBase * m.bTypeMult * m.bRoll)) * 100) / 100;
  if (mod === 1) return '';
  if (e.helpers && e.helpers.length > 1) {
    return ` x ${mult(mod)} (${e.helpers.length} helpers: ${e.helpers.map(name).join(', ')})`;
  }
  const who = m.bWithBase === undefined ? name(e.gang) : `with ${name(e.gang)}`;
  return ` x ${mult(mod)} (${who})`;
}

function numbers(m: MatchupResult, bNote = ''): string {
  let s = `${sides(m, bNote)}.`;
  if (m.crit) s += ' A critical hit!';
  if (m.superEffective) s += " It's super effective!";
  return s;
}

const VERB: Record<MatchupResult['kind'], string> = {
  block: 'blocks',
  coverage: 'runs at',
  contested: 'fights for the ball with',
  tackle: 'meets',
  throw: 'throws past',
  kick: 'kicks over',
};

function describe(
  e: PlayEvent,
  name: (u: UnitId) => string,
  who: (u: UnitId) => Combatant,
  los: number,
): string | null {
  const plus = (at: [number, number]) => `+${Math.round(at[0] - los)}`;
  switch (e.type) {
    case 'snap':
    case 'move':
    case 'whistle':
      return null;
    case 'spend': {
      const move = who(e.unit).moves.find((m) => m.id === e.moveId);
      const bang = move?.effect.kind === 'attack' ? '!' : '';
      return `${name(e.unit)} uses ${move?.name ?? e.moveId}${bang} (${e.ppLeft} left)${bang ? '' : '.'}`;
    }
    case 'miss': {
      const move = who(e.unit).moves.find((m) => m.id === e.moveId);
      return `${name(e.unit)}'s ${move?.name ?? e.moveId} missed!`;
    }
    case 'hp':
      switch (e.cause) {
        case 'battle':
          return `${name(e.unit)} loses ${-e.delta} HP (${e.hp} left).`;
        case 'recoil':
          return `${name(e.unit)} is hurt by recoil: ${e.delta} HP (${e.hp} left).`;
        case 'drain':
          return `${name(e.unit)} drains ${e.delta} HP (${e.hp} left).`;
        case 'self-ko':
          return `${name(e.unit)} is worn out: 0 HP for the game.`;
        // The line drain shows on the HP bars, not in the text box: four lines every snap is noise.
        case 'line':
          return '';
      }
      break;
    case 'battle':
      // A pile's tackle (slice 10o): the tackler waited on the stopped carrier.
      if (e.pile) {
        return `${name(e.b)} waits on ${name(e.a)} and meets him as he goes: ${numbers(e.matchup, gangNote(e, name))}`;
      }
      return `${name(e.a)} ${VERB[e.matchup.kind]} ${name(e.b)}: ${numbers(e.matchup, gangNote(e, name))}`;
    case 'hold':
      return e.pancake
        ? `${name(e.unit)} pancakes ${name(e.rusher)}!`
        : `${name(e.unit)} holds ${name(e.rusher)} until ${secs(e.until)}.`;
    case 'shed':
      if (e.screen && e.blocker) return `${name(e.blocker)} lets ${name(e.unit)} through.`;
      return e.blocker
        ? `${name(e.unit)} sheds ${name(e.blocker)} at ${secs(e.t)}.`
        : `${name(e.unit)} comes in unblocked.`;
    case 'pressure':
      return `${name(e.unit)} gets to the passer at ${secs(e.t)}.`;
    case 'open':
      return `${name(e.unit)} is open by ${stepsText(e.steps)} step${e.steps === 1 ? '' : 's'} at ${secs(e.t)}.`;
    case 'covered':
      return `${name(e.by)} sits on ${name(e.unit)}. Covered.`;
    case 'sack':
      return `${name(e.by)} sacks ${name(e.unit)} for a loss of ${-e.yards}.`;
    case 'throw':
      return (
        `${name(e.from)} throws ${e.hot ? 'hot ' : ''}to ${name(e.to)} at ${secs(e.t)}` +
        `${e.pressured ? ' under pressure' : ''}: ${throwOdds(e)}. ` +
        `${e.onTarget ? 'On target.' : 'Off target.'}`
      );
    case 'throw-away':
      return `${name(e.unit)} throws it away under pressure.`;
    case 'catch':
      return `${name(e.unit)} makes the catch at ${plus(e.at)}.`;
    case 'incomplete':
      return e.by ? `${name(e.by)} breaks it up. Incomplete.` : 'Incomplete.';
    case 'interception':
      return `Intercepted by ${name(e.unit)}!`;
    case 'handoff':
      return `${name(e.from)} hands off to ${name(e.to)}.`;
    case 'hole':
      if (e.behind === null) return `${name(e.unit)} bounces it to the edge.`;
      return e.cutback
        ? `${name(e.unit)} cuts back behind ${name(e.behind)}!`
        : `${name(e.unit)} hits the hole behind ${name(e.behind)}.`;
    case 'tackle': {
      // Down where he stood (slice 10o): met while stopped, no battle fought, so no one "brings him down".
      if (e.stopped) {
        return `${listNames(e.by.map(name))} ${e.by.length === 1 ? 'gets' : 'get'} to ${name(e.unit)} while he is stopped. Down at ${plus(e.at)}.`;
      }
      // A gang tackle (slice 10k) names both tacklers, and the verb agrees; a per-man pile (slice
      // 10q) names every man counted ("Tauros, Starmie and Gengar").
      const one = e.by.length === 1;
      return e.outOfBounds
        ? `${listNames(e.by.map(name))} ${one ? 'pushes' : 'push'} ${name(e.unit)} out of bounds at ${plus(e.at)}.`
        : `${listNames(e.by.map(name))} ${one ? 'brings' : 'bring'} ${name(e.unit)} down at ${plus(e.at)}.`;
    }
    case 'broken-tackle':
      // A broken pile (slice 10o) speaks once, on the tackler's event, naming every man beaten.
      if (e.pile && e.pile.length > 1) {
        if (e.pile[0] !== e.by) return '';
        return `${name(e.unit)} breaks the pile! ${listNames(e.pile.map(name))} are beaten.`;
      }
      return `${name(e.unit)} breaks the tackle!`;
    case 'fumble':
      return e.with
        ? `${name(e.by)} and ${name(e.with)} knock the ball loose from ${name(e.unit)}! ${name(e.recoveredBy)} recovers.`
        : `${name(e.by)} strips ${name(e.unit)}! ${name(e.recoveredBy)} recovers.`;
  }
}

/** "Tauros, Starmie and Gengar". */
const listNames = (xs: readonly string[]): string =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`;

const ORDINAL = ['', '1st', '2nd', '3rd', '4th'];

/** "your 32", "midfield", "the opponent's 42". */
export function spotName(ballOn: number): string {
  const y = Math.round(ballOn);
  if (y === 50) return 'midfield';
  return y < 50 ? `your ${y}` : `the opponent's ${100 - y}`;
}

function summary(o: PlayOutcome): string {
  const gain = `${o.yards >= 0 ? '+' : ''}${o.yards}`;
  switch (o.result) {
    case 'touchdown':
      return `Result: ${gain}, touchdown!`;
    case 'safety':
      return 'Result: safety.';
    case 'interception':
      return 'Result: interception, change of possession.';
    case 'fumble':
      return 'Result: fumble lost, change of possession.';
    default:
      break;
  }
  if (o.turnoverOnDowns) return `Result: ${gain}, turnover on downs.`;
  const s = o.state;
  const toGo = s.toGo >= 100 - s.ballOn ? 'goal' : String(s.toGo);
  const head = o.result === 'incomplete' ? 'incomplete' : gain;
  return `Result: ${head}, ${ORDINAL[s.down] ?? `${s.down}th`} and ${toGo} at ${spotName(s.ballOn)}.`;
}

/**
 * A spot named from one team's side, for lines that name both teams: "own 25", "midfield", or
 * the other side's name and yard line ("Team Gengar's 40", passed as `opponent`).
 */
export function sideSpotName(ballOn: number, opponent: string): string {
  const y = Math.round(ballOn);
  if (y === 50) return 'midfield';
  return y < 50 ? `own ${y}` : `${opponent} ${100 - y}`;
}

/** Seconds left as a game clock: "4:35", "0:07". */
export function clockName(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "Q2 4:35", "OT", "OT2". */
export function periodName(quarter: number, clock: number, quarters = 4): string {
  if (quarter <= quarters) return `Q${quarter} ${clockName(clock)}`;
  const p = quarter - quarters;
  return p === 1 ? 'OT' : `OT${p}`;
}

/** "1st & 10", "3rd & goal". */
export function downName(down: number, toGo: number, ballOn: number): string {
  const d = ORDINAL[down] ?? `${down}th`;
  return `${d} & ${toGo >= 100 - ballOn ? 'goal' : toGo}`;
}

/**
 * One line for each game event that is not a snap (a snap has its own play log): kicks, scores,
 * possession changes, quarters, overtime, and the final. `who` names a unit on the kicking side.
 */
export function gameEventLine(
  e: GameEvent,
  teams: Record<TeamId, Team>,
  who?: (u: UnitId) => string,
): string | null {
  const team = (t: TeamId) => teams[t].name;
  const kicker = (u: UnitId) => (who ? who(u) : 'The kicker');
  const score = (s: Record<TeamId, number>) => `${team('A')} ${s.A}, ${team('B')} ${s.B}`;
  switch (e.type) {
    case 'coin-toss':
      return `${team(e.receiving)} win the toss and will receive.`;
    case 'kickoff':
      return `${team(e.kicking)} kick off. ${team(e.receiving)} start at their ${e.ballOn}.`;
    case 'punt':
      if (e.blocked) return `${kicker(e.kicker)}'s punt is blocked!`;
      return `${kicker(e.kicker)} punts ${e.yards} yards${e.touchback ? ' into the end zone. Touchback.' : '.'}`;
    case 'field-goal':
    case 'extra-point': {
      const what =
        e.type === 'extra-point' ? 'extra point' : `${Math.round(e.distance)}-yard field goal`;
      if (e.blocked) return `${kicker(e.kicker)}'s ${what} is blocked!`;
      const odds = `${Math.round(e.accuracy * 100)}%${e.pressured ? ', rushed' : ''}`;
      return `${kicker(e.kicker)}'s ${what} is ${e.good ? 'good' : 'no good'} (${odds}).`;
    }
    case 'score':
      return `${SCORE_NAME[e.kind]}, ${team(e.team)}! ${score(e.score)}.`;
    case 'possession':
      return `${team(e.team)} take over at ${sideSpotName(e.ballOn, `${team(e.team === 'A' ? 'B' : 'A')}'s`)} (${POSSESSION_NAME[e.reason]}).`;
    case 'quarter-end':
      return e.quarter === 2 ? null : `End of quarter ${e.quarter}. ${score(e.score)}.`;
    case 'halftime':
      return `Halftime. ${score(e.score)}. Everyone recovers half their HP.`;
    case 'overtime':
      return `Overtime${e.period > 1 ? ` period ${e.period}` : ''}! ${team(e.first)} start at the opponent's ${100 - e.ballOn}.`;
    case 'final':
      return `Final: ${score(e.score)}. ${team(e.winner)} win.`;
    case 'sub': {
      // Plain English, no numbers (slice 11c): "Team Electabuzz brings Rhydon in at DL1."
      const mon = (i: number) => teams[e.team].roster[i]?.name ?? `#${i}`;
      if (e.swap) {
        const and = e.from ? ` and ${mon(e.out)} to ${e.from}` : '';
        return `${team(e.team)} moves ${mon(e.in)} to ${e.slot}${and}.`;
      }
      const regen = e.regenerated ? ` ${mon(e.out)} regenerates on the bench.` : '';
      const cured = e.cured ? ` ${mon(e.out)}'s status is cured.` : '';
      return `${team(e.team)} brings ${mon(e.in)} in at ${e.slot}.${regen}${cured}`;
    }
    case 'rest': {
      const c = teams[e.team].roster[e.target];
      const move = c?.moves.find((m) => m.id === e.moveId)?.name ?? e.moveId;
      const sit = e.sitPlays > 0 ? ` and sits ${e.sitPlays} plays` : '';
      return `${team(e.team)}: ${c?.name ?? `#${e.target}`} uses ${move} on the bench${sit}.`;
    }
    case 'bag': {
      const item = itemName(e.item);
      const name = teams[e.team].roster[e.target]?.name ?? `#${e.target}`;
      return `${team(e.team)} use a ${item} on ${name}.`;
    }
    case 'snap':
      return null;
  }
}

const itemName = (id: string): string =>
  (balance.items as unknown as Record<string, { name?: string } | undefined>)[id]?.name ?? id;

const SCORE_NAME: Record<ScoreKind, string> = {
  touchdown: 'Touchdown',
  'extra-point': 'Extra point',
  'two-point': 'Two-point conversion',
  'field-goal': 'Field goal',
  safety: 'Safety',
};

const POSSESSION_NAME: Record<PossessionReason, string> = {
  kickoff: 'kickoff',
  interception: 'interception',
  fumble: 'fumble recovery',
  downs: 'turnover on downs',
  punt: 'punt',
  'missed-field-goal': 'missed field goal',
  overtime: 'overtime possession',
};
